"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ComponentProps, ReactNode, RefObject } from "react";
import { createPortal } from "react-dom";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { cn } from "@/lib/utils";
import { useIsPhone } from "@/hooks/useIsPhone";
import { getStagedWindow, useDesktopStore } from "@/stores/desktopStore";
import { DESKTOP_Z_INDEX, DOCK_LAYOUT, ISLAND_LAYOUT } from "./desktopUI.constants";
import { useIslandMotion } from "./useIslandMotion";
import type { SpotlightRequest } from "@/components/GlobalSearchDialog";
import { Button, Glyph, SearchTrigger, Tooltip, TopBarIsland } from "@/components/desktop/ui";
import { createLazyOverlay } from "@/components/desktop/ui/createLazyOverlay";
import type { QuickAsk as QuickAskComponent } from "./QuickAsk";
import type { WaitingAnswerReporter as WaitingAnswerReporterComponent } from "./useWaitingAnswer";
import { useWaitingConversationStore } from "@/stores/waitingConversationStore";
import { askInShipmentPanel } from "@/stores/shipmentChatPanelStore";
import { IslandWilson, useIslandWilson } from "./IslandWilson";
import { NO_ASK, askIdAfterClose, nextAskId } from "./askRequest.utils";
import { WILSON_BAR_TRANSITION } from "./viewTransition.utils";
import { PaneShownContext } from "@/components/desktop/ui/usePaneBack";
import { IslandWidthContext } from "@/components/desktop/ui/islandLane.utils";
import { TopBarContext, type AskOptions, type ConversationRequest } from "./TopBarContext";
type TopBarSlotName = "back" | "left" | "leftMeta" | "right" | "controls";
type SlotClaims = Record<TopBarSlotName, number>;
export const PageStagedContext = createContext(true);
const EMPTY_SLOTS: Record<TopBarSlotName, HTMLElement | null> = {
  back: null,
  left: null,
  leftMeta: null,
  right: null,
  controls: null
};
const NO_CLAIMS: SlotClaims = {
  back: 0,
  left: 0,
  leftMeta: 0,
  right: 0,
  controls: 0
};
export function TopBarProvider({
  children,
  onOpenSearch,
  onOpenSpotlight,
  onOpenConversation,
  isSpotlightOpen = false,
  isSpotlightSearching = false
}: {
  children: ReactNode;
  onOpenSearch: () => void;
  onOpenSpotlight?: (request: SpotlightRequest) => void;
  onOpenConversation?: (request: ConversationRequest) => void;
  isSpotlightOpen?: boolean;
  isSpotlightSearching?: boolean;
}) {
  const [slots, setSlots] = useState(EMPTY_SLOTS);
  const [claims, setClaims] = useState(NO_CLAIMS);
  const [wilsonSubject, setWilsonSubject] = useState("");
  const [scopeDocumentType, setScopeDocumentType] = useState("");
  const [islandWidth, setIslandWidth] = useState(0);
  const [askId, setAskId] = useState(NO_ASK);
  const lastAskIdRef = useRef(NO_ASK);
  const hasAskRequest = askId !== NO_ASK;
  const [askingQuestion, setAskingQuestion] = useState("");
  const [askingSubject, setAskingSubject] = useState<string | null>(null);
  const [isAskFromSpotlight, setIsAskFromSpotlight] = useState(false);
  if (isAskFromSpotlight && !isSpotlightOpen) setIsAskFromSpotlight(false);
  const isPhone = useIsPhone();
  const isInlineAskAvailable = !isPhone && (!isSpotlightOpen || isAskFromSpotlight);
  const isAsking = hasAskRequest && isInlineAskAvailable;
  const hasWaitingAnswer = useWaitingConversationStore(state => state.waiting !== null);
  const registerSlot = useCallback((name: TopBarSlotName, el: HTMLElement | null) => setSlots(prev => prev[name] === el ? prev : {
    ...prev,
    [name]: el
  }), []);
  const claimSlot = useCallback((name: TopBarSlotName) => {
    setClaims(prev => ({
      ...prev,
      [name]: prev[name] + 1
    }));
    return () => setClaims(prev => ({
      ...prev,
      [name]: prev[name] - 1
    }));
  }, []);
  const hasStagedWindow = useDesktopStore(state => !!getStagedWindow(state.windows));
  const hasPlace = hasStagedWindow || claims.back + claims.left + claims.leftMeta > 0;
  const scopeSubject = hasPlace ? wilsonSubject : "";
  const openSpotlight = useCallback((request: SpotlightRequest) => {
    if (onOpenSpotlight) onOpenSpotlight(request);else onOpenSearch();
  }, [onOpenSpotlight, onOpenSearch]);
  const openConversation = useCallback((request: ConversationRequest) => {
    if (onOpenConversation) onOpenConversation(request);else openSpotlight({
      question: request.question
    });
  }, [onOpenConversation, openSpotlight]);
  const askInline = useCallback((question?: string, options?: AskOptions) => {
    if (askInShipmentPanel(question)) return;
    setAskingQuestion(question ?? "");
    setAskingSubject(options?.subject ?? null);
    lastAskIdRef.current = nextAskId(lastAskIdRef.current);
    setAskId(lastAskIdRef.current);
    setIsAskFromSpotlight(isSpotlightOpen);
  }, [isSpotlightOpen]);
  const startAsking = isPhone ? null : askInline;
  const stopAsking = useCallback((closingAskId: number) => setAskId(currentAskId => askIdAfterClose({
    currentAskId,
    closingAskId
  })), []);
  useEffect(() => {
    if (!hasPlace || !isPhone || isSpotlightOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.key === "k") {
        event.preventDefault();
        event.stopImmediatePropagation();
        openConversation({});
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [hasPlace, isPhone, isSpotlightOpen, openConversation]);
  const value = useMemo(() => ({
    slots,
    registerSlot,
    claimSlot,
    claims,
    openSearch: onOpenSearch,
    openSpotlight,
    isSpotlightOpen,
    isSpotlightSearching,
    setWilsonSubject,
    wilsonSubject,
    setScopeDocumentType,
    scopeSubject,
    scopeDocumentType: scopeSubject ? scopeDocumentType : "",
    setIslandWidth,
    openConversation,
    isAsking,
    hasAskRequest,
    askId,
    askingQuestion,
    askingSubject,
    startAsking,
    stopAsking
  }), [slots, registerSlot, claimSlot, claims, onOpenSearch, openSpotlight, isSpotlightOpen, isSpotlightSearching, wilsonSubject, scopeSubject, scopeDocumentType, openConversation, isAsking, hasAskRequest, askId, askingQuestion, askingSubject, startAsking, stopAsking]);
  return <TopBarContext.Provider value={value} data-sentry-element="TopBarContext.Provider" data-sentry-component="TopBarProvider" data-sentry-source-file="TopBar.tsx">
            <IslandWidthContext.Provider value={islandWidth} data-sentry-element="IslandWidthContext.Provider" data-sentry-source-file="TopBar.tsx">
                {children}
                {hasWaitingAnswer && <WaitingAnswerReporter isEnabled={isInlineAskAvailable} />}
            </IslandWidthContext.Provider>
        </TopBarContext.Provider>;
}
const QuickAsk = createLazyOverlay<ComponentProps<typeof QuickAskComponent>>({
  name: "quickAsk",
  load: () => import("./QuickAsk"),
  exportName: "QuickAsk",
  renderFallback: () => null
});
const WaitingAnswerReporter = createLazyOverlay<ComponentProps<typeof WaitingAnswerReporterComponent>>({
  name: "waitingAnswerReporter",
  load: () => import("./useWaitingAnswer"),
  exportName: "WaitingAnswerReporter",
  renderFallback: () => null
});
const ISLAND_STYLE = {
  top: ISLAND_LAYOUT.TOP,
  height: ISLAND_LAYOUT.HEIGHT,
  zIndex: DESKTOP_Z_INDEX.DOCK
};
const ISLAND_CENTERED = "left-1/2 max-w-[calc(100vw-32px)] -translate-x-1/2 justify-center";
const ISLAND_CONTENT = "relative flex items-center gap-1";
const PHONE_NAV_BAR = "left-4";
const PHONE_EDGE = 16;
const ISLAND_CONTENT_CENTERED = "max-w-[calc(100vw-40px)] flex-shrink-0";
const FIELD = "flex h-8 min-w-0 items-center gap-1 rounded-full bg-ink/[0.08] p-1";
const RECORD_TOKEN = "flex h-6 min-w-0 flex-shrink items-center gap-1 rounded-xl bg-ink/[0.12] pr-2";
export function TopBar() {
  const t = useClientTranslations("search");
  const tPage = useClientTranslations("desktop.page");
  const showDesktop = useDesktopStore(state => state.showDesktop);
  const ctx = useContext(TopBarContext);
  const isPhone = useIsPhone();
  const registerSlot = ctx?.registerSlot;
  const slotRefs = useMemo(() => ({
    back: (el: HTMLElement | null) => registerSlot?.("back", el),
    left: (el: HTMLElement | null) => registerSlot?.("left", el),
    leftMeta: (el: HTMLElement | null) => registerSlot?.("leftMeta", el),
    right: (el: HTMLElement | null) => registerSlot?.("right", el),
    controls: (el: HTMLElement | null) => registerSlot?.("controls", el)
  }), [registerSlot]);
  const hasStagedWindow = useDesktopStore(state => !!getStagedWindow(state.windows));
  const isAsking = !!ctx?.isAsking;
  const {
    view: wilsonView,
    openAnswer
  } = useIslandWilson();
  const headerRef = useRef<HTMLElement>(null);
  const hasPlace = hasStagedWindow || !!ctx && (ctx.claims.back > 0 || ctx.claims.left > 0 || ctx.claims.leftMeta > 0);
  const contentRef = useRef<HTMLDivElement>(null);
  useIslandMotion({
    islandRef: headerRef,
    contentRef
  });
  useReportIslandWidth({
    islandRef: headerRef,
    contentRef,
    setIslandWidth: ctx?.setIslandWidth
  });
  if (!ctx) return null;
  const {
    claims,
    openSearch
  } = ctx;
  const hasChip = claims.left > 0;
  const isNavBar = isPhone && hasPlace;
  const isHidden = isAsking || ctx.isSpotlightOpen;
  const fieldText = wilsonView.phase === "working" ? wilsonView.line : wilsonView.phase === "landed" ? t("wilsonAnswered") : t("askOrSearch");
  const onFieldClick = wilsonView.phase !== "idle" ? openAnswer : openSearch;
  const islandStyle = isNavBar ? {
    ...ISLAND_STYLE,
    right: PHONE_EDGE
  } : ISLAND_STYLE;
  return <TopBarIsland style={islandStyle} ref={headerRef} className={cn("fixed", !ctx.isSpotlightOpen && WILSON_BAR_TRANSITION, isAsking && "[&:not(:has(>div>:not(.hidden)))]:invisible", "group/island data-[attached=true]:w-[min(560px,calc(100vw-32px))] data-[attached=true]:rounded-b-none data-[attached=true]:rounded-t-panel data-[attached=true]:bg-island data-[attached=true]:shadow-one-piece", isNavBar ? PHONE_NAV_BAR : ISLAND_CENTERED)} data-sentry-element="TopBarIsland" data-sentry-component="TopBar" data-sentry-source-file="TopBar.tsx">
            <div ref={contentRef} className={cn(ISLAND_CONTENT, isNavBar ? "w-full min-w-0" : cn(ISLAND_CONTENT_CENTERED, "group-data-[attached=true]/island:flex-1"))}>
                {!isHidden && <IslandWilson />}
                {!isHidden && hasPlace && !isNavBar && <Tooltip side="bottom" content={tPage("desktop")}>
                        <Button variant="control" size="icon" onClick={showDesktop} aria-label={tPage("desktop")} className="flex-shrink-0">
                            <Glyph name="home" />
                        </Button>
                    </Tooltip>}
                <div data-island-stretch className={cn(FIELD, isNavBar ? "flex-1" : "min-w-[420px] group-data-[attached=true]/island:flex-1", isHidden && "hidden")}>
                    <div className={cn(RECORD_TOKEN, !hasPlace && "hidden")}>
                        <div ref={slotRefs.back} className={cn("flex flex-shrink-0 items-center", claims.back === 0 && "hidden")} />
                        <div className={cn("flex min-w-0 items-center", isNavBar ? "flex-shrink" : "max-w-[40vw]", !hasChip && "hidden")}>
                            <div ref={slotRefs.left} className="flex min-w-0 items-center gap-2" />
                        </div>
                        <div ref={slotRefs.leftMeta} className={cn("light-tokens flex flex-shrink-0 items-center", (claims.leftMeta === 0 || isNavBar) && "hidden")} />
                    </div>
                    <SearchTrigger placement="island" placeholder={fieldText} label={wilsonView.phase === "idle" ? t("searchLabel") : `${fieldText}. ${t("wilsonOpenAnswer")}`} shortcut={isPhone || wilsonView.phase !== "idle" ? undefined : "⌘K"} ariaKeyShortcuts="Meta+K" leading={wilsonView.phase === "idle" ? undefined : <span aria-hidden />} onClick={onFieldClick} className={cn("h-6 w-auto min-w-0 flex-1 bg-transparent pl-2 hover:bg-transparent", wilsonView.phase === "working" && "wilson-shimmer")} data-sentry-element="SearchTrigger" data-sentry-source-file="TopBar.tsx" />
                </div>
                {ctx.hasAskRequest && <QuickAsk key={ctx.askId} anchorRef={headerRef} shouldYield={!isAsking} subject={ctx.askingSubject ?? ctx.scopeSubject} initialQuestion={ctx.askingQuestion} onClose={() => ctx.stopAsking(ctx.askId)} openConversation={ctx.openConversation} bottomReserve={hasPlace ? ISLAND_LAYOUT.TOP * 2 : DOCK_LAYOUT.HEIGHT + 40} />}
                {isNavBar && <span className="min-w-0 flex-1" aria-hidden />}
                <div ref={slotRefs.right} className={cn("flex items-center gap-0.5", (claims.right === 0 || isAsking) && "hidden")} />
                <div ref={slotRefs.controls} className={cn("flex items-center gap-0.5", (claims.controls === 0 || isNavBar || isAsking) && "hidden")} />
            </div>
        </TopBarIsland>;
}
function useReportIslandWidth({
  islandRef,
  contentRef,
  setIslandWidth
}: {
  islandRef: RefObject<HTMLElement | null>;
  contentRef: RefObject<HTMLElement | null>;
  setIslandWidth: ((width: number) => void) | undefined;
}) {
  useEffect(() => {
    const island = islandRef.current;
    const content = contentRef.current;
    if (!island || !content || !setIslandWidth || typeof ResizeObserver !== "function" || typeof getComputedStyle !== "function") {
      return;
    }
    const report = () => {
      const {
        paddingLeft,
        paddingRight
      } = getComputedStyle(island);
      setIslandWidth(Math.round(content.offsetWidth + parseFloat(paddingLeft) + parseFloat(paddingRight)));
    };
    report();
    const observer = new ResizeObserver(report);
    observer.observe(content);
    return () => observer.disconnect();
  }, [islandRef, contentRef, setIslandWidth]);
}
export function TopBarSlot({
  slot,
  subject,
  scopeDocumentType,
  children
}: {
  slot: TopBarSlotName;
  subject?: string;
  scopeDocumentType?: string;
  children: ReactNode;
}) {
  const ctx = useContext(TopBarContext);
  const isPageStaged = useContext(PageStagedContext);
  const isPaneShown = useContext(PaneShownContext);
  const isStaged = isPageStaged && isPaneShown;
  const claimSlot = ctx?.claimSlot;
  useEffect(() => {
    if (!isStaged || !claimSlot) return;
    return claimSlot(slot);
  }, [slot, isStaged, claimSlot]);
  const setWilsonSubject = ctx?.setWilsonSubject;
  useEffect(() => {
    if (slot !== "left" || !isStaged || !setWilsonSubject) return;
    setWilsonSubject(subject ?? "");
    return () => setWilsonSubject("");
  }, [slot, isStaged, subject, setWilsonSubject]);
  const setScopeDocumentType = ctx?.setScopeDocumentType;
  useEffect(() => {
    if (slot !== "left" || !isStaged || !setScopeDocumentType) return;
    setScopeDocumentType(scopeDocumentType ?? "");
    return () => setScopeDocumentType("");
  }, [slot, isStaged, scopeDocumentType, setScopeDocumentType]);
  const el = ctx?.slots[slot];
  if (!isStaged || !el) return null;
  return createPortal(children, el);
}
export function useFindShortcut(inputRef: RefObject<HTMLInputElement | null>) {
  const isPageStaged = useContext(PageStagedContext);
  const isPaneShown = useContext(PaneShownContext);
  const isStaged = isPageStaged && isPaneShown;
  useEffect(() => {
    if (!isStaged) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key !== "f") return;
      const input = inputRef.current;
      if (!input) return;
      event.preventDefault();
      input.focus();
      input.select();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isStaged, inputRef]);
}